import { Loader2, Plus, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { type UploadFolder, uploadService } from '@/services/upload.service';

interface MultiImageUploaderProps {
  /** Array of S3 keys stored in database */
  value: string[];
  /** Called with array of S3 keys for storage */
  onChange: (keys: string[]) => void;
  folder: UploadFolder;
  entityId?: string;
  label?: string;
  maxImages?: number;
  accept?: string;
  className?: string;
  /** Display URLs (full CloudFront URLs) - use these for showing images */
  displayUrls?: string[];
  /** Minimum recommended width for quality display */
  recommendedMinWidth?: number;
  /** Minimum recommended height for quality display */
  recommendedMinHeight?: number;
}

// Helper to check image dimensions
const checkImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      resolve({ width: 0, height: 0 });
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(file);
  });
};

export function MultiImageUploader({
  value = [],
  onChange,
  folder,
  entityId,
  label = 'Upload Images',
  maxImages = 10,
  accept = 'image/*',
  className = '',
  displayUrls = [],
  recommendedMinWidth,
  recommendedMinHeight,
}: MultiImageUploaderProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  // Track display URLs for newly uploaded files (key -> displayUrl mapping)
  const [localDisplayUrls, setLocalDisplayUrls] = useState<Record<string, string>>({});

  // Get display URL for a key (either from prop or from local state)
  const getDisplayUrl = (key: string, index: number): string => {
    // First check if we have a corresponding displayUrl from props
    if (displayUrls[index]) {
      return displayUrls[index];
    }
    // Otherwise check local state for newly uploaded files
    if (localDisplayUrls[key]) {
      return localDisplayUrls[key];
    }
    // Fallback to key itself (shouldn't normally happen)
    return key;
  };

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;

      const remainingSlots = maxImages - value.length;
      const filesToUpload = files.slice(0, remainingSlots);

      if (filesToUpload.length === 0) {
        setError(`Maximum ${maxImages} images allowed`);
        return;
      }

      setIsUploading(true);
      setError(null);
      setWarning(null);

      // Check image dimensions for quality warning
      if (recommendedMinWidth || recommendedMinHeight) {
        const lowResImages: string[] = [];
        for (const file of filesToUpload) {
          if (file.type.startsWith('image/')) {
            const { width, height } = await checkImageDimensions(file);
            if (width > 0 && height > 0) {
              const isBelowWidth = recommendedMinWidth && width < recommendedMinWidth;
              const isBelowHeight = recommendedMinHeight && height < recommendedMinHeight;
              if (isBelowWidth || isBelowHeight) {
                lowResImages.push(`${file.name} (${width}×${height}px)`);
              }
            }
          }
        }
        if (lowResImages.length > 0) {
          setWarning(
            `Low resolution: ${lowResImages.join(', ')}. For best quality, use at least ${recommendedMinWidth || 0}×${recommendedMinHeight || 0}px. Images may appear blurry or distorted.`
          );
        }
      }

      try {
        const uploadResults = await uploadService.uploadFiles(filesToUpload, folder, entityId);
        // Extract keys for storage and track display URLs locally
        const newKeys = uploadResults.map((r) => r.key);
        const newLocalUrls: Record<string, string> = {};
        uploadResults.forEach((r) => {
          newLocalUrls[r.key] = r.fileUrl;
        });
        setLocalDisplayUrls((prev) => ({ ...prev, ...newLocalUrls }));
        onChange([...value, ...newKeys]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload failed');
      } finally {
        setIsUploading(false);
        // Reset input
        e.target.value = '';
      }
    },
    [folder, entityId, onChange, value, maxImages, recommendedMinWidth, recommendedMinHeight]
  );

  const handleRemove = useCallback(
    (index: number) => {
      const removedKey = value[index];
      const newValue = [...value];
      newValue.splice(index, 1);
      onChange(newValue);
      // Clean up local display URL for removed key
      if (removedKey && localDisplayUrls[removedKey]) {
        setLocalDisplayUrls((prev) => {
          const newUrls = { ...prev };
          delete newUrls[removedKey];
          return newUrls;
        });
      }
    },
    [value, onChange, localDisplayUrls]
  );

  const canAddMore = value.length < maxImages;

  return (
    <div className={className}>
      {label && (
        <span className="mb-[8px] block text-[14px] font-medium text-[#181818]">
          {label}
          <span className="ml-[8px] text-[12px] font-normal text-[#8d8c8f]">
            ({value.length}/{maxImages})
          </span>
        </span>
      )}

      <div className="flex flex-wrap gap-[12px]">
        {value.map((key, index) => (
          <div key={key} className="relative">
            <img
              src={getDisplayUrl(key, index)}
              alt={`Upload ${index + 1}`}
              className="h-[100px] w-[100px] rounded-[8px] border border-[#e5e5e5] object-cover"
            />
            <button
              type="button"
              onClick={() => handleRemove(index)}
              className="absolute -right-[6px] -top-[6px] flex h-[20px] w-[20px] items-center justify-center rounded-full bg-[#d4183d] text-white shadow-md transition-colors hover:bg-[#b91c1c]"
            >
              <X size={12} />
            </button>
          </div>
        ))}

        {canAddMore && (
          <label className="flex h-[100px] w-[100px] cursor-pointer flex-col items-center justify-center rounded-[8px] border-2 border-dashed border-[#e5e5e5] bg-[#fafafa] transition-colors hover:border-[#055135] hover:bg-[#f5f5f5]">
            {isUploading ? (
              <Loader2 size={20} className="animate-spin text-[#055135]" />
            ) : (
              <>
                <Plus size={20} className="text-[#8d8c8f]" />
                <span className="mt-[4px] text-[10px] text-[#8d8c8f]">Add</span>
              </>
            )}
            <input
              type="file"
              accept={accept}
              multiple
              onChange={handleFileChange}
              disabled={isUploading}
              className="hidden"
            />
          </label>
        )}
      </div>

      {error && <p className="mt-[8px] text-[12px] text-[#d4183d]">{error}</p>}
      {warning && <p className="mt-[8px] text-[12px] text-[#b45309]">{warning}</p>}
    </div>
  );
}
